import { useEffect } from "react";
import { SITE_CONFIG } from "../app/config";

const BASE_URL = `https://${SITE_CONFIG.domain}`;

type HreflangEntry = { lang: string; href: string };

type HeadConfig = {
  title: string;
  description: string;
  path?: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noIndex?: boolean;
  hreflang?: HreflangEntry[];
};



const setMetaTag = (attribute: "name" | "property", key: string, content: string): void => {
  let el = document.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attribute, key);
    document.head.appendChild(el);
  }
  el.content = content;
};

const setCanonical = (url: string | null): void => {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (url) {
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = url;
  } else {
    link?.remove();
  }
};

const setJsonLd = (data: Record<string, unknown> | Record<string, unknown>[] | undefined): void => {
  let script = document.querySelector('script[data-seo="json-ld"]') as HTMLScriptElement | null;
  if (data) {
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seo = "json-ld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
  } else {
    script?.remove();
  }
};

const setHreflang = (entries: HreflangEntry[] | undefined): void => {
  // Remove all existing hreflang links first to avoid stale tags
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());

  if (entries) {
    for (const { lang, href } of entries) {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = lang;
      link.href = href;
      document.head.appendChild(link);
    }
  }
};

const setRobotsMeta = (noIndex: boolean): void => {
  let el = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
  if (noIndex) {
    if (!el) {
      el = document.createElement("meta");
      el.name = "robots";
      document.head.appendChild(el);
    }
    el.content = "noindex, nofollow";
  } else {
    el?.remove();
  }
};

/**
 * Dynamically manages document <head> for SEO.
 * Sets title, meta description, canonical URL, Open Graph, Twitter cards, and JSON-LD.
 */
export const useHead = ({ title, description, path, ogImage, ogType, jsonLd, noIndex, hreflang }: HeadConfig): void => {
  useEffect(() => {
    const fullTitle = `${title} | ${SITE_CONFIG.name}`;
    const canonicalUrl = path ? `${BASE_URL}${path}` : null;
    const image = ogImage ?? `${BASE_URL}/social-preview.svg`;

    // Document title
    document.title = fullTitle;

    // Meta tags
    setMetaTag("name", "description", description);

    // Canonical URL
    setCanonical(canonicalUrl);

    // Hreflang (only set on multilingual pages, e.g. homepage)
    setHreflang(hreflang);

    // Open Graph
    setMetaTag("property", "og:title", fullTitle);
    setMetaTag("property", "og:description", description);
    setMetaTag("property", "og:type", ogType ?? "website");
    setMetaTag("property", "og:image", image);
    if (canonicalUrl) setMetaTag("property", "og:url", canonicalUrl);

    // Twitter Card
    setMetaTag("name", "twitter:title", fullTitle);
    setMetaTag("name", "twitter:description", description);

    // JSON-LD structured data
    setJsonLd(jsonLd);

    // Robots
    setRobotsMeta(noIndex ?? false);

    return () => {
      // Reset to homepage defaults on unmount so stale meta tags don't persist
      const defaultTitle = `${SITE_CONFIG.name} — Turn Spotify Playlists into Vinyl & CD Shopping Lists`;
      const defaultDescription =
        "Turn your Spotify playlist into a vinyl & CD shopping list. Find where to buy physical records, CDs and vinyls with real Discogs prices. Own your music collection forever.";

      document.title = defaultTitle;
      setMetaTag("name", "description", defaultDescription);
      setCanonical(`${BASE_URL}/`);
      setHreflang(undefined);
      setMetaTag("property", "og:title", defaultTitle);
      setMetaTag("property", "og:description", defaultDescription);
      setMetaTag("property", "og:type", "website");
      setMetaTag("property", "og:url", `${BASE_URL}/`);
      setMetaTag("property", "og:image", `${BASE_URL}/social-preview.svg`);
      setMetaTag("name", "twitter:title", defaultTitle);
      setMetaTag("name", "twitter:description", defaultDescription);
      setJsonLd(undefined);
      setRobotsMeta(false);
    };
  }, [title, description, path, ogImage, ogType, jsonLd, noIndex, hreflang]);
};

export { BASE_URL };
